import { useTranslate } from "@tolgee/react";
import { UserRound } from "lucide-react";
import { motion } from "motion/react";
import { formatUsd } from "#/lib/formatters";
import { cn } from "#/lib/utils";

/**
 * "Fees flow to you" illustration for the logged-out gate, built from real
 * product anatomy instead of a node graph: an overlapping avatar stack (your
 * friends) sends a fee chip along a dashed connector into a live stat card.
 * The card shows the same estimate the earnings simulator computes, so
 * moving the slider updates the illustration — it's a display, not a prop.
 */

// Distinct calm hues per avatar (theme-tier denotive tokens, dark-aware).
// Deliberately NOT status green/red — these are people, not positions.
const AVATAR_TINTS = [
	"bg-[var(--color-theme-denotives-skyblue)]/15 text-[var(--color-theme-denotives-skyblue)]",
	"bg-[var(--color-theme-denotives-violet)]/15 text-[var(--color-theme-denotives-violet)]",
	"bg-[var(--color-theme-denotives-orange)]/15 text-[var(--color-theme-denotives-orange)]",
] as const;

export function ReferralFlowDiagram({
	monthlyRebate,
	tradeRebate,
}: {
	/** Live simulator estimate shown in the stat card; null hides the value. */
	monthlyRebate: number | null;
	/** Illustrative rebate on a single friend trade, rides the connector. */
	tradeRebate: number | null;
}) {
	const { t } = useTranslate();

	return (
		<div className="flex items-center gap-3">
			<div className="shrink-0">
				<div className="flex -space-x-2.5">
					{AVATAR_TINTS.map((tint) => (
						<span
							key={tint}
							className={cn(
								"flex size-9 items-center justify-center rounded-full ring-2 ring-background",
								tint,
							)}
						>
							<UserRound className="size-4" strokeWidth={1.75} />
						</span>
					))}
				</div>
				<p className="mt-1.5 text-center text-caption1-medium text-muted-foreground">
					{t("referrals.gate.flow-friends")}
				</p>
			</div>

			{/* dashed connector; a fee chip travels it and is absorbed by the card */}
			<div className="relative min-w-10 flex-1" aria-hidden="true">
				<svg aria-hidden="true" className="block h-px w-full overflow-visible">
					<line
						x1="0"
						y1="0.5"
						x2="100%"
						y2="0.5"
						strokeWidth="1.25"
						strokeDasharray="3 5"
						strokeLinecap="round"
						className="stroke-border"
					/>
				</svg>
				{tradeRebate != null && tradeRebate > 0 && (
					<motion.span
						className="-translate-y-1/2 absolute top-1/2 left-0 whitespace-nowrap rounded-full bg-primary/10 px-2 py-0.5 text-caption1-tabular-bold text-primary motion-reduce:hidden"
						initial={{ left: "-8%", opacity: 0 }}
						animate={{
							left: ["-8%", "18%", "78%", "102%"],
							opacity: [0, 1, 1, 0],
						}}
						transition={{
							duration: 3,
							times: [0, 0.2, 0.82, 1],
							ease: "easeInOut",
							repeat: Number.POSITIVE_INFINITY,
							repeatDelay: 1.2,
						}}
					>
						+{formatUsd(tradeRebate)}
					</motion.span>
				)}
			</div>

			<div className="shrink-0 rounded-xl border border-border bg-background px-4 py-3 shadow-sm">
				<p className="text-caption1-medium text-muted-foreground">
					{t("referrals.gate.sim-result")}
				</p>
				<p className="mt-0.5 text-title2 text-primary tabular-nums">
					{monthlyRebate != null ? formatUsd(monthlyRebate) : "—"}
				</p>
			</div>
		</div>
	);
}
